import {inject, observer} from 'mobx-react';
import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import GenerateMicroreelUI from '../view/GenerateMicroreelUI';
import GenerateMicroreelStore from '../store/GenerateMicroreelStore';
import CoursesRepository from '../../../../data/repositories/CoursesRepository';
import {Subject} from '../../../../data/models/Course/LearningStandards';
import {
  MicroreelGenerationModel,
  MicroreelSlideGenerationModel,
  MicroreelVideoGenerationModel,
} from '../../../../data/models/Microreels/MicroreelGenerationModel';
import MyLibraryModalStore from '../../../../modules/MyLibraryModal/store/MyLibraryModalStore';
import {MyLibraryModal} from '../../../../modules/MyLibraryModal';
import {
  MEDIA_FILES_AUDIO,
  MEDIA_FILES_DOCUMENT,
  MEDIA_FILES_DOCUMENT_AND_VIDEO,
  MediaFile,
} from '../../../../data/models/LibraryFile/MediaFile';
import {toastConfig} from '../../../../uiToolkit/Toast/toastConfig';
import {generateMicroreelText} from '../generateMicroreelText';
import {
  MicroreelGenerationInfo,
  storeGenerationInfo,
} from '../../../Microreels/Generating/generatingTypes';

interface Props {
  generateMicroreelStore?: GenerateMicroreelStore;
  myLibraryModalStore?: MyLibraryModalStore;
}

// generate_from_slide accepts the backend's TextFileTypes (PDF, DOCX, PPTX).
const SLIDE_SOURCE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];
// Directly uploaded videos join the same generate_from_video route as
// library-picked ones (same formats the library modal upload takes).
const VIDEO_SOURCE_TYPES = ['video/mp4', 'video/x-m4v'];
const coursesRepository = new CoursesRepository();

const GenerateMicroreelContainer = inject(
  'generateMicroreelStore',
  'myLibraryModalStore',
)(
  observer(({generateMicroreelStore, myLibraryModalStore}: Props) => {
    const [isGenerationAvailable, setGenerationAvailable] = useState(false);
    const [generationStarted, setGenerationStarted] = useState(false);
    // Covers the lookup that runs before the store starts loading.
    const [submitting, setSubmitting] = useState(false);
    const [subjects, setSubjects] = useState<Subject[]>([]);
    const [subjectsLoading, setSubjectsLoading] = useState(false);
    const [subjectsError, setSubjectsError] = useState(false);
    const [subjectsRevision, setSubjectsRevision] = useState(0);
    const [redirect, setRedirect] = useState<{path: string; state?: any}>();
    const {organizationId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const project = location.state as {
      projectId?: string;
      projectName?: string;
    } | null;

    useEffect(() => {
      setSubjects([]);
    }, [organizationId]);

    useEffect(() => {
      let active = true;
      setSubjectsError(false);
      setSubjectsLoading(!!organizationId);
      if (organizationId) {
        coursesRepository
          .getLearingStandarts(organizationId)
          .then(result => {
            if (!active) return;
            if (result.success && result.data) setSubjects(result.data);
            else setSubjectsError(true);
          })
          .catch(() => {
            if (active) setSubjectsError(true);
          })
          .finally(() => {
            if (active) setSubjectsLoading(false);
          });
      }
      return () => {
        active = false;
      };
    }, [organizationId, subjectsRevision]);

    useEffect(() => {
      if (!redirect) return;
      if (location.pathname === redirect.path) {
        setRedirect(undefined);
        return;
      }
      navigate(redirect.path, {state: redirect.state});
    }, [redirect, navigate, location.pathname]);

    useEffect(() => {
      let active = true;

      const checkAvailability = async (orgId: string) => {
        const result =
          await generateMicroreelStore?.checkGenerationAvailability(orgId);
        if (!active) return;
        const available = result ?? false;
        setGenerationAvailable(available);
        if (!available) {
          toast.error(generateMicroreelText.generationUnavailable, toastConfig);
        }
      };

      if (organizationId) {
        checkAvailability(organizationId);
      }

      return () => {
        active = false;
      };
    }, [generateMicroreelStore, organizationId]);

    const onCancel = () => {
      navigate(-1);
    };

    // Leaving the page aborts any in-flight source upload poll: the store is
    // a singleton and a poll left running would keep the whole form locked
    // on the next visit.
    useEffect(
      () => () => {
        generateMicroreelStore!.abortSourceUpload();
      },
      [],
    );

    const browseLibrary = useCallback(
      (
        sourceType: number,
        initialFilter?: number,
      ): Promise<MediaFile | undefined> =>
        new Promise(res => {
          myLibraryModalStore!.setInitialSelectedFilter(initialFilter);
          myLibraryModalStore!.setOnCancelCallback(() => res(undefined));
          myLibraryModalStore!.showLibrary(sourceType, files => {
            if (files.length > 0) {
              res(files[0]);
            }
          });
        }),
      [myLibraryModalStore],
    );

    // The library picker offers both documents (generate_from_slide) and
    // videos (generate_from_video) as source material.
    const onBrowseDocument = useCallback(async (): Promise<
      MediaFile | undefined
    > => {
      const file = await browseLibrary(
        MEDIA_FILES_DOCUMENT_AND_VIDEO,
        MEDIA_FILES_DOCUMENT,
      );
      if (
        file &&
        !SLIDE_SOURCE_TYPES.includes(file.fileType) &&
        !file.fileType.startsWith('video')
      ) {
        toast.error(generateMicroreelText.invalidSourceType, toastConfig);
        return undefined;
      }
      return file;
    }, [browseLibrary]);

    const onUploadDocument = useCallback(
      async (file: File): Promise<MediaFile | undefined> => {
        if (!organizationId) return undefined;
        if (
          !SLIDE_SOURCE_TYPES.includes(file.type) &&
          !VIDEO_SOURCE_TYPES.includes(file.type)
        ) {
          toast.error(generateMicroreelText.invalidSourceType, toastConfig);
          return undefined;
        }
        return generateMicroreelStore!.uploadSourceDocument(
          organizationId,
          file,
        );
      },
      [organizationId],
    );

    const onBrowseAudio = useCallback(
      (): Promise<MediaFile | undefined> => browseLibrary(MEDIA_FILES_AUDIO),
      [browseLibrary],
    );

    const onGenerationStarted = (info: MicroreelGenerationInfo) => {
      setGenerationStarted(true);
      const projectInfo = {
        ...info,
        projectId: project?.projectId,
        projectName: project?.projectName,
      };
      const stored = organizationId
        ? storeGenerationInfo(organizationId, projectInfo)
        : projectInfo;
      setRedirect({
        path: `/manage/${organizationId}/microreels/generating`,
        state: stored,
      });
    };

    // The newest group id is captured before generating: the generating page
    // compares it against the newest group when the poll ends to tell a
    // successful generation apart from a failed one (no new group created).
    const onGenerateFromPrompt = async (
      model: MicroreelGenerationModel,
      info: MicroreelGenerationInfo,
    ) => {
      if (!organizationId) return;
      setSubmitting(true);
      try {
        info.previousGroupId =
          (await generateMicroreelStore!.getLatestGroupId(organizationId)) ??
          null;
        const result = await generateMicroreelStore!.generateFromPrompt(
          organizationId,
          {...model, projectId: project?.projectId},
        );
        if (result.started) {
          info.generationId = result.generationId;
          onGenerationStarted(info);
        }
      } finally {
        setSubmitting(false);
      }
    };

    const onGenerateFromSlide = async (
      model: MicroreelSlideGenerationModel,
      info: MicroreelGenerationInfo,
    ) => {
      if (!organizationId) return;
      setSubmitting(true);
      try {
        info.previousGroupId =
          (await generateMicroreelStore!.getLatestGroupId(organizationId)) ??
          null;
        const result = await generateMicroreelStore!.generateFromSlide(
          organizationId,
          {...model, projectId: project?.projectId},
        );
        if (result.started) {
          info.generationId = result.generationId;
          onGenerationStarted(info);
        }
      } finally {
        setSubmitting(false);
      }
    };

    const onGenerateFromVideo = async (
      model: MicroreelVideoGenerationModel,
      info: MicroreelGenerationInfo,
    ) => {
      if (!organizationId) return;
      setSubmitting(true);
      try {
        info.previousGroupId =
          (await generateMicroreelStore!.getLatestGroupId(organizationId)) ??
          null;
        const result = await generateMicroreelStore!.generateFromVideo(
          organizationId,
          {...model, projectId: project?.projectId},
        );
        if (result.started) {
          info.generationId = result.generationId;
          onGenerationStarted(info);
        }
      } finally {
        setSubmitting(false);
      }
    };

    const onViewMicroreels = () => {
      navigate(`/manage/${organizationId}/microreels`);
    };

    const onSuggestTitle = useCallback(
      async (fileName: string): Promise<string> => {
        if (!organizationId) return '';
        return generateMicroreelStore!.getAvailableGroupTitle(
          organizationId,
          fileName,
        );
      },
      [organizationId],
    );

    return (
      <>
        <GenerateMicroreelUI
          key={organizationId}
          subjects={subjects}
          subjectsLoading={subjectsLoading}
          subjectsError={subjectsError}
          onReloadSubjects={() => setSubjectsRevision(value => value + 1)}
          isLoading={generateMicroreelStore!.isLoading || submitting}
          onCancel={onCancel}
          onGenerateFromPrompt={onGenerateFromPrompt}
          onGenerateFromSlide={onGenerateFromSlide}
          onGenerateFromVideo={onGenerateFromVideo}
          onBrowseDocument={onBrowseDocument}
          onUploadDocument={onUploadDocument}
          onBrowseAudio={onBrowseAudio}
          onSuggestTitle={onSuggestTitle}
          onViewMicroreels={onViewMicroreels}
          isGenerationAvailable={isGenerationAvailable}
          generationStarted={generationStarted}
        />
        <MyLibraryModal />
      </>
    );
  }),
);

export default GenerateMicroreelContainer;
